import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { faqs } from '../../data/webServicesData';
import AnimateIn from '../AnimateIn';

const FaqSection = () => {
  const { language, t } = useLanguage();
  const [open, setOpen] = useState(null);

  return (
    <section className="py-20 md:py-28 px-6 lg:px-8" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        <AnimateIn className="text-center mb-14">
          <p className="accent-tag justify-center">{t('wsFaqTag')}</p>
          <h2
            className="text-3xl md:text-4xl font-bold tracking-[-0.02em] text-white"
            style={{ fontFamily: "'Syne', sans-serif" }}
            data-testid="faq-title"
          >
            {t('wsFaqTitle')}
          </h2>
        </AnimateIn>

        <div className="space-y-3">
          {faqs.map((faq, i) => {
            const isOpen = open === i;
            return (
              <AnimateIn key={i} delay={i * 0.06}>
                <div
                  className="rounded-2xl border transition-all duration-300"
                  style={{
                    borderColor: isOpen ? 'rgba(226,193,121,0.5)' : 'rgba(255,255,255,0.1)',
                    background: isOpen ? 'rgba(226,193,121,0.05)' : 'rgba(255,255,255,0.03)',
                    boxShadow: isOpen ? '0 8px 30px rgba(226,193,121,0.08)' : 'none',
                  }}
                >
                  <button
                    onClick={() => setOpen(isOpen ? null : i)}
                    className="w-full flex items-center justify-between gap-4 text-left p-5 md:p-6"
                    aria-expanded={isOpen}
                    data-testid={`faq-item-${i}`}
                  >
                    <span className="text-sm md:text-base font-semibold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
                      {faq.q[language]}
                    </span>
                    <span
                      className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                        isOpen ? 'rotate-45 text-black' : 'text-gray-400'
                      }`}
                      style={
                        isOpen
                          ? { background: 'linear-gradient(135deg, #706958, #e2c179)' }
                          : { border: '1px solid rgba(255,255,255,0.15)' }
                      }
                    >
                      <Plus size={14} />
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="px-5 md:px-6 pb-6 text-sm leading-relaxed text-gray-400" data-testid={`faq-answer-${i}`}>
                          {faq.a[language]}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </AnimateIn>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
